import React, { useState } from 'react';

/**
 * Analysis Provenance Panel
 * ------------------------------------------------------------
 * Reads the backend `swarm_trace` (populated when an analysis was generated
 * via the BCM Agent Swarm) and renders a compact "who did what" receipt.
 *
 * Shape of `swarm_trace`:
 *   {
 *     total_duration_sec: 64.8,
 *     agents: [
 *       { agent: "market_sizing", model: "openai/gpt-4o", duration_sec: 18.2, success: true, error: null },
 *       ...
 *     ]
 *   }
 */

const AGENT_META = {
  market_sizing: {
    label: 'Market Sizing',
    persona: 'Dr. Nora Vance',
    color: 'bg-blue-600',
    text: 'text-blue-700',
    phase: 'Phase 1',
  },
  competitive_intelligence: {
    label: 'Competitive Intelligence',
    persona: 'Marcus Chen',
    color: 'bg-purple-600',
    text: 'text-purple-700',
    phase: 'Phase 1',
  },
  resonate_segmentation: {
    label: 'Resonate rAI Segmentation',
    persona: 'Sofia Ramos',
    color: 'bg-pink-600',
    text: 'text-pink-700',
    phase: 'Phase 1',
    featured: true,
  },
  opportunities: {
    label: 'Opportunities',
    persona: 'Kai Morgan',
    color: 'bg-emerald-600',
    text: 'text-emerald-700',
    phase: 'Phase 1',
  },
  threats: {
    label: 'Risk & Threats',
    persona: 'Ivan Petrov',
    color: 'bg-rose-600',
    text: 'text-rose-700',
    phase: 'Phase 1',
  },
  recommendations: {
    label: 'Strategic Recommendations',
    persona: 'Elena Park',
    color: 'bg-orange-600',
    text: 'text-orange-700',
    phase: 'Phase 1',
  },
  data_sources: {
    label: 'Data Sources & Methodology',
    persona: 'James Okonkwo',
    color: 'bg-teal-600',
    text: 'text-teal-700',
    phase: 'Phase 1',
  },
  executive_summary_synthesis: {
    label: 'Executive Summary Synthesis',
    persona: 'Priya Mehta',
    color: 'bg-indigo-600',
    text: 'text-indigo-700',
    phase: 'Phase 2',
    featured: true,
  },
};

const initialsOf = (fullName) =>
  fullName
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatModel = (model) => {
  if (!model || model === 'none') return '—';
  // e.g. "openai/gpt-4o" -> "GPT-4o"
  const parts = model.split('/');
  const name = parts[parts.length - 1] || model;
  return name
    .replace('gpt-4o', 'GPT-4o')
    .replace('gpt-5.2', 'GPT-5.2')
    .replace('claude-sonnet-4-20250514', 'Claude Sonnet 4')
    .replace('claude-4-sonnet-20250514', 'Claude Sonnet 4')
    .replace('gemini-2.5-pro', 'Gemini 2.5 Pro');
};

const StatusPill = ({ success, error }) => {
  if (success) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
        OK
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700"
      title={error || 'Agent failed'}
    >
      Failed
    </span>
  );
};

const AgentRow = ({ trace }) => {
  const meta = AGENT_META[trace.agent] || {
    label: trace.agent,
    persona: trace.agent,
    color: 'bg-gray-500',
    text: 'text-gray-700',
    phase: '—',
  };

  return (
    <div
      data-testid={`provenance-row-${trace.agent}`}
      className={`flex items-center gap-3 p-2.5 rounded-lg border ${
        trace.success ? 'bg-white border-gray-200' : 'bg-rose-50 border-rose-200'
      }`}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[11px] shadow-sm ${meta.color}`}
      >
        {initialsOf(meta.persona)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`text-xs font-bold truncate ${meta.text}`}>{meta.label}</span>
          {meta.featured && (
            <span className="text-[9px] uppercase tracking-wide bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
              specialist
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 truncate">
          {meta.persona} · {meta.phase}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[11px] font-mono text-gray-700">{formatModel(trace.model)}</div>
        <div className="text-[10px] text-gray-500">
          {typeof trace.duration_sec === 'number' ? `${trace.duration_sec.toFixed(1)}s` : '—'}
        </div>
      </div>
      <div className="shrink-0">
        <StatusPill success={trace.success} error={trace.error} />
      </div>
    </div>
  );
};

const AnalysisProvenance = ({ swarmTrace }) => {
  const [expanded, setExpanded] = useState(false);

  const summary = (() => {
    if (!swarmTrace || !Array.isArray(swarmTrace.agents)) {
      return null;
    }
    const agents = swarmTrace.agents;
    const total = agents.length;
    const ok = agents.filter((a) => a.success).length;
    const failed = total - ok;
    const uniqueModels = Array.from(
      new Set(agents.filter((a) => a.success).map((a) => formatModel(a.model)))
    );
    return {
      total,
      ok,
      failed,
      uniqueModels,
      totalDuration: swarmTrace.total_duration_sec,
    };
  })();

  // Legacy analyses have no swarm trace — render a minimal notice so users
  // still see the panel slot and know where future provenance will appear.
  if (!summary) {
    return (
      <div
        data-testid="analysis-provenance-panel"
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-3"
      >
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="text-xl" aria-hidden="true">🐝</span>
          <span>
            <span className="font-semibold text-gray-700">Analysis Provenance:</span>{' '}
            Legacy analysis — no agent-swarm trace available. Re-run this analysis to
            generate an 8-agent provenance trace.
          </span>
        </div>
      </div>
    );
  }

  const phase1 = swarmTrace.agents.filter(
    (a) => AGENT_META[a.agent]?.phase === 'Phase 1'
  );
  const phase2 = swarmTrace.agents.filter(
    (a) => AGENT_META[a.agent]?.phase === 'Phase 2'
  );

  return (
    <div
      data-testid="analysis-provenance-panel"
      className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
    >
      {/* Collapsed header — always visible */}
      <button
        data-testid="provenance-toggle-btn"
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl" aria-hidden="true">
            🐝
          </span>
          <div>
            <div className="text-sm font-bold text-gray-900">Analysis Provenance</div>
            <div className="text-xs text-gray-600">
              {summary.ok}/{summary.total} agents succeeded
              {summary.failed > 0 && (
                <span className="text-rose-600 font-semibold"> · {summary.failed} failed</span>
              )}
              {typeof summary.totalDuration === 'number' && (
                <span> · {summary.totalDuration.toFixed(1)}s total</span>
              )}
              {summary.uniqueModels.length > 0 && (
                <span> · {summary.uniqueModels.join(' + ')}</span>
              )}
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
          {phase1.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                Phase 1 · Specialists (parallel)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {phase1.map((a) => (
                  <AgentRow key={a.agent} trace={a} />
                ))}
              </div>
            </div>
          )}
          {phase2.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                Phase 2 · Synthesis
              </div>
              <div className="grid grid-cols-1 gap-2">
                {phase2.map((a) => (
                  <AgentRow key={a.agent} trace={a} />
                ))}
              </div>
            </div>
          )}
          <div className="text-[11px] text-gray-500 pt-2 border-t border-gray-200">
            BCM Agent Swarm · each specialist produces one section of the analysis, then a
            synthesis agent writes the executive summary. Failed specialists fall back to a
            secondary model automatically.
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisProvenance;
